import React, { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { FormattedMenuSectionInfo } from 'api'

interface SortableItemsContainerProps {
  items: FormattedMenuSectionInfo[]
  onItemsReorder: (newItems: FormattedMenuSectionInfo[]) => void
  children: React.ReactNode
  isPrinterClient?: boolean
  onDragStateChange?: (isDragging: boolean) => void
}

const SortableItemsContainer: React.FC<SortableItemsContainerProps> = ({
  items,
  onItemsReorder,
  children,
  isPrinterClient,
  onDragStateChange,
}) => {
  const [isAnyItemDragging, setIsAnyItemDragging] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (_event: DragStartEvent) => {
    setIsAnyItemDragging(true)
    onDragStateChange?.(true)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setIsAnyItemDragging(false)
    onDragStateChange?.(false)
    
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = items.findIndex(item => item.id === active.id)
    const newIndex = items.findIndex(item => item.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Update local state immediately for responsive UI
    const newItems = arrayMove(items, oldIndex, newIndex)
    onItemsReorder(newItems)
  }

  // Only enable drag and drop for Printer MMR clients
  if (!isPrinterClient) {
    return <>{children}</>
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { isAnyItemDragging } as any)
          }
          return child
        })}
      </SortableContext>
    </DndContext>
  )
}

export default SortableItemsContainer 